/*
 * AI 填表员「看示例」回放 Modal —— 大气版。
 *
 * 用录制的真实对话(demoScript)在弹窗里重放一遍「上传 → 多轮对话 → 填好表格」
 * 全流程，替代录屏。左侧 5 步进度 + 右侧对话流式打字(DemoPlaybackView)。
 * 控制条：播放/暂停 · 倍速 · 重放。
 *
 * 样式 scope 在 .aiClerk-demoReplay-* 前缀下。
 */
import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import DemoPlaybackView from './DemoPlaybackView'
import { trackClick } from '../utils/track'
import './DemoReplayModal.scss'

interface Props {
  visible: boolean
  onClose: () => void
}

const FIXED_SPEED = 4

/**
 * 弹窗内所有交互埋点统一走 trackClick(`ai_click_web_ai_form_filler`),
 * 跟 view_demo 同一个 event 名 —— V1 已大量验证,神策侧必然能处理;
 * 不用 trackDialogShow/trackDialogClick 是怕"demo_replay" 这种新 popTitle
 * 没在神策侧注册时会被丢弃。弹窗"打开"行为由外层的 view_demo 那条 click 表达,
 * 此处不再重复曝光埋点。
 */
const DEMO_TRACK_SIGN = 'aai.b8134.click.ai_click_web_ai_form_filler'

const DemoReplayModal: React.FC<Props> = ({ visible, onClose }) => {
  const speed = FIXED_SPEED
  const [playing, setPlaying] = useState<boolean>(true)
  const [runKey, setRunKey] = useState<number>(0)
  const [progress, setProgress] = useState<number>(0)
  const [ended, setEnded] = useState<boolean>(false)
  // 关闭时取观看进度 + 是否看完一次;state 在事件外可能滞后,所以用 ref 兜底
  const progressRef = useRef(progress)
  const endedRef = useRef(ended)
  useEffect(() => {
    progressRef.current = progress
  }, [progress])
  useEffect(() => {
    endedRef.current = ended
  }, [ended])

  if (!visible) return null

  /** 统一关闭:埋一次 click,带当前观看进度(0~100)与是否完整看完过一次 */
  const handleClose = () => {
    trackClick('formFiller', 'demo_close', {
      requirement_id: 1103,
      track_sign: DEMO_TRACK_SIGN,
      number1: Math.round(progressRef.current * 100),
      bool1: endedRef.current
    })
    onClose()
  }

  const handleReplay = () => {
    trackClick('formFiller', 'demo_replay', {
      requirement_id: 1103,
      track_sign: DEMO_TRACK_SIGN
    })
    setProgress(0)
    setEnded(false)
    setPlaying(true)
    setRunKey((k) => k + 1)
  }

  return ReactDOM.createPortal(
    <div className="aiClerk-demoReplay-mask" onClick={handleClose}>
      <div
        className="aiClerk-demoReplay"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="aiClerk-demoReplay__head">
          <div className="aiClerk-demoReplay__head-left">
            <span className="aiClerk-demoReplay__badge">示例演示</span>
            <span className="aiClerk-demoReplay__title">
              AI 填表员 · 看一遍它怎么把照片填进表格
            </span>
          </div>
          <button
            type="button"
            className="aiClerk-demoReplay__close"
            onClick={handleClose}
            aria-label="关闭"
          >
            ✕
          </button>
        </div>

        {/* 内容：真回放视图 */}
        <div className="aiClerk-demoReplay__body">
          <DemoPlaybackView
            playing={playing}
            speed={speed}
            runKey={runKey}
            onProgress={setProgress}
            onEnded={() => {
              setEnded(true)
              setPlaying(false)
              trackClick('formFiller', 'demo_ended', {
                requirement_id: 1103,
                track_sign: DEMO_TRACK_SIGN
              })
            }}
          />
        </div>

        {/* 控制条 */}
        <div className="aiClerk-demoReplay__controls">
          {ended ? (
            <button
              type="button"
              className="aiClerk-demoReplay__ctrl-btn is-primary"
              onClick={handleReplay}
            >
              ↻ 再看一遍
            </button>
          ) : (
            <button
              type="button"
              className="aiClerk-demoReplay__ctrl-btn"
              onClick={() => {
                setPlaying((p) => {
                  // 上报"切换到下一状态"的动作:p 当前是 playing,点击后变为 !p
                  trackClick('formFiller', p ? 'demo_pause' : 'demo_play', {
                    requirement_id: 1103,
                    track_sign: DEMO_TRACK_SIGN,
                    number1: Math.round(progressRef.current * 100)
                  })
                  return !p
                })
              }}
            >
              {playing ? '⏸ 暂停' : '▶ 播放'}
            </button>
          )}
          <button
            type="button"
            className="aiClerk-demoReplay__ctrl-btn"
            onClick={handleReplay}
          >
            ↻ 重放
          </button>

          <div className="aiClerk-demoReplay__progress">
            <div
              className="aiClerk-demoReplay__progress-fill"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default DemoReplayModal
